import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const MAX_CHARS = 5000;

interface ReplyComposerProps {
  onSend: (body: string) => Promise<void>;
  loading?: boolean;
  placeholder?: string;
}

const ReplyComposer: React.FC<ReplyComposerProps> = ({ onSend, loading, placeholder }) => {
  const { t } = useTranslation('emailHub');
  const [value, setValue] = useState('');
  const [sending, setSending] = useState(false);

  const isLoading = loading || sending;
  const trimmed = value.trim();
  const canSend = trimmed.length > 0 && trimmed.length <= MAX_CHARS && !isLoading;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await onSend(trimmed);
      setValue('');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canSend) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="email-hub-reply-composer">
      <label className="email-hub-reply-composer__label">
        {t('reply.label', 'Reply')}
      </label>
      <div className="email-hub-reply-composer__box">
        <textarea
          className="email-hub-reply-composer__textarea"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder ?? t('reply.placeholder', 'Type your reply...')}
          maxLength={MAX_CHARS}
          rows={5}
          disabled={isLoading}
        />
        <div className="email-hub-reply-composer__footer">
          <span className={`email-hub-reply-composer__count ${value.length > MAX_CHARS * 0.9 ? 'email-hub-reply-composer__count--warn' : ''}`}>
            {value.length} / {MAX_CHARS}
          </span>
          <span className="email-hub-reply-composer__hint">
            {t('reply.hint', 'Ctrl+Enter to send')}
          </span>
          <button
            className="email-hub-reply-composer__send"
            onClick={handleSend}
            disabled={!canSend}
          >
            {isLoading ? (
              <span className="email-hub-spinner" />
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 2 11 13" />
                <path d="m22 2-7 20-4-9-9-4 20-7z" />
              </svg>
            )}
            {t('reply.send', 'Send Reply')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReplyComposer;
